import { useIsSingleExternalLinkOwnerEnabled } from '@/app-shell/flags/desktop-flags';
import { logger } from '@/utils/logger/logger';
import { useEffect } from 'react';
import { setupExternalLinkHandler } from './externalLinkHandler';

/**
 * Arms the external link handler, and disarms it when its kill switch is
 * flipped. Renders nothing.
 *
 * This lives in a component rather than the module-scope `isTauri()` block in
 * `_app.tsx`, because a flag cannot be read outside a render — so setup now
 * runs on the first commit instead of at module evaluation. The flag's default
 * resolves synchronously, so that is one render pass, not a wait on Flagsmith.
 *
 * What that costs is asymmetric, and only half of it is backstopped. Anchor
 * clicks are: the listener the opener plugin injects claims them, there and
 * before any setup at all. `window.open` is not — nothing else patches it, so
 * a `window.open` in that pass reaches the webview. Only a render-phase side
 * effect could get there; the widgets this override exists for (Intercom) load
 * long after.
 */
export function ExternalLinkHandlerSync(): null {
  const enabled = useIsSingleExternalLinkOwnerEnabled();

  useEffect(() => {
    if (!enabled) return;

    // Setup is async, so a flag that flips back before it resolves would leave
    // an installed handler with nothing left to dispose it.
    let disposed = false;
    let dispose: (() => void) | undefined;

    void setupExternalLinkHandler()
      .then((disposer) => {
        if (disposed) {
          disposer();
          return;
        }
        dispose = disposer;
      })
      .catch(logger.error);

    return () => {
      disposed = true;
      dispose?.();
    };
  }, [enabled]);

  return null;
}
